'use client';

import React, { useState, useRef, useCallback, useEffect, useId } from 'react';
import classnames from 'classnames';

import usePositioning from '../../../hooks/usePositioning';
import Typography from '../Typography/Typography';
import styles from './Tooltip.module.scss';

type TooltipSide = 'top' | 'right' | 'bottom' | 'left';
type Theme = 'dark' | 'light';

export interface TooltipProps {
  /** The content displayed inside the tooltip popup */
  content: React.ReactNode;
  /** The trigger element that the tooltip wraps */
  children: React.ReactElement;
  /** Which side of the trigger the tooltip appears on */
  side?: TooltipSide;
  /** Visual theme */
  theme?: Theme;
  /** Delay in ms before the tooltip appears on hover */
  delayDuration?: number;
  /** Whether the tooltip is disabled */
  disabled?: boolean;
  /** Maximum width of the tooltip content in px */
  maxWidth?: number;
  /** Additional class name for the tooltip container */
  className?: string;
  /** Test identifier */
  dataTestId?: string;
}

/**
 * @component Tooltip
 * @param {TooltipProps} props - Tooltip properties
 * @returns {React.ReactElement} A tooltip component inspired by shadcn/Radix UI
 * @example
 * <Tooltip content="Add to library" side="top">
 *   <button>Hover me</button>
 * </Tooltip>
 */
const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  theme = 'dark',
  delayDuration = 200,
  disabled = false,
  maxWidth = 300,
  className,
  dataTestId = 'tooltip',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const { computedSide, positionStyle, arrowStyle, updatePosition, wrapperRef, floatingRef } =
    usePositioning({ side, gap: 8, enabled: isVisible });

  const showTooltip = useCallback(() => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delayDuration);
  }, [delayDuration, disabled]);

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  }, []);

  // Recalculate position after tooltip renders
  useEffect(() => {
    if (!isVisible) return;

    // Use requestAnimationFrame to wait for the DOM to paint
    const raf = requestAnimationFrame(() => {
      updatePosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [isVisible, updatePosition]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={classnames(styles.wrapper, className)}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      data-testid={dataTestId}
    >
      {React.cloneElement(children, {
        'aria-describedby': isVisible ? tooltipId : undefined,
      } as React.HTMLAttributes<HTMLElement>)}
      {isVisible && (
        <div
          ref={floatingRef}
          id={tooltipId}
          role="tooltip"
          className={classnames(styles.tooltip, styles[computedSide], styles[theme])}
          // eslint-disable-next-line react/forbid-dom-props -- dynamic positioning and max-width from usePositioning hook
          style={{ ...positionStyle, maxWidth: `${maxWidth}px` }}
          data-testid={`${dataTestId}-content`}
        >
          <Typography variant="body" size="small" className={styles.content}>
            {content}
          </Typography>
          <span
            className={classnames(styles.arrow, styles[`arrow-${computedSide}`])}
            // eslint-disable-next-line react/forbid-dom-props -- dynamic arrow positioning from usePositioning hook
            style={arrowStyle}
          />
        </div>
      )}
    </div>
  );
};

export default Tooltip;
